import React, { useEffect, useRef } from 'react';

/**
 * Opens Cashfree hosted checkout via official JS SDK (v3).
 * For redirect checkout (_self), Cashfree navigates to PG; return_url handles completion.
 */
const CashfreePayment = ({
  paymentSessionId,
  orderId,
  onSuccess,
  onFailure,
  onClose,
  returnPath = '/stay-payment-return'
}) => {
  const checkoutStartedRef = useRef(false);

  useEffect(() => {
    if (!paymentSessionId) return undefined;

    const sessionId = String(paymentSessionId).trim();
    if (!sessionId || sessionId.includes('http')) {
      onFailure?.(new Error('Invalid payment session from server'));
      return undefined;
    }

    if (orderId) {
      sessionStorage.setItem('cashfree_order_id', orderId);
    }

    let cancelled = false;

    const openCheckout = () => {
      if (cancelled || checkoutStartedRef.current || !window.Cashfree) return;

      checkoutStartedRef.current = true;

      try {
        const cashfree = window.Cashfree({
          mode: process.env.REACT_APP_CASHFREE_ENVIRONMENT || 'sandbox'
        });

        const checkoutPromise = cashfree.checkout({
          paymentSessionId: sessionId,
          redirectTarget: '_self'
        });

        if (!checkoutPromise || typeof checkoutPromise.then !== 'function') {
          return;
        }

        checkoutPromise
          .then((result) => {
            if (cancelled) return;

            if (result?.error) {
              console.error('Cashfree checkout error:', result.error);
              checkoutStartedRef.current = false;
              onFailure?.(result.error);
              return;
            }

            const status =
              result?.order?.orderStatus ||
              result?.paymentDetails?.paymentStatus ||
              result?.orderStatus;

            const resolvedOrderId =
              result?.order?.orderId ||
              result?.order?.order_id ||
              result?.orderId;

            if (status === 'PAID' || status === 'SUCCESS') {
              const targetId = resolvedOrderId || orderId;
              if (targetId) {
                window.location.href = `${window.location.origin}${returnPath}?order_id=${encodeURIComponent(targetId)}`;
              } else {
                onSuccess?.(result);
              }
              return;
            }

            // Non-terminal result (e.g. user closed modal) — stay on booking review
            if (status === 'FAILED' || status === 'CANCELLED' || status === 'USER_DROPPED') {
              checkoutStartedRef.current = false;
              onFailure?.(new Error(result?.message || 'Payment was not completed'));
            }
          })
          .catch((err) => {
            if (cancelled) return;
            checkoutStartedRef.current = false;
            console.error('Cashfree checkout failed:', err);
            onFailure?.(err);
          });
      } catch (err) {
        checkoutStartedRef.current = false;
        console.error('Cashfree initialization error:', err);
        onFailure?.(err);
      }
    };

    const ensureSdk = () =>
      new Promise((resolve, reject) => {
        if (window.Cashfree) {
          resolve();
          return;
        }

        const existing = document.querySelector('script[data-bfp-cashfree-sdk]');
        if (existing) {
          if (window.Cashfree) {
            resolve();
            return;
          }
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener(
            'error',
            () => reject(new Error('Failed to load payment gateway')),
            { once: true }
          );
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.async = true;
        script.dataset.bfpCashfreeSdk = '1';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load payment gateway'));
        document.body.appendChild(script);
      });

    ensureSdk()
      .then(() => {
        if (!cancelled) openCheckout();
      })
      .catch((err) => {
        if (!cancelled) onFailure?.(err);
      });

    return () => {
      cancelled = true;
    };
  }, [paymentSessionId, orderId, returnPath, onSuccess, onFailure]);

  if (!paymentSessionId) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-4" />
          <p className="text-gray-600">Loading payment gateway...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Complete Payment</h2>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          )}
        </div>

        <div className="mb-4 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-4" />
          <p className="text-gray-600">Opening Cashfree secure payment…</p>
        </div>

        <p className="text-xs text-gray-500 text-center">
          Your payment information is secure and encrypted
        </p>
      </div>
    </div>
  );
};

export default CashfreePayment;
